import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ResponsiveList } from '../components/ResponsiveList';

const pageCardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};
const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
  minHeight: 44,
};
const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 500,
  minHeight: 44,
};

export function Enquiries() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<{ subject: string; to: string; body: string } | null>(null);

  const { data: emails = [], isLoading } = useQuery({
    queryKey: ['emails'],
    queryFn: () => api.listEmails(),
  });

  const ingestMutation = useMutation({
    mutationFn: () => api.ingestEmails(25),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['emails'] }),
  });
  const draftMutation = useMutation({
    mutationFn: (gmailId: string) => api.draftReply(gmailId),
    onSuccess: (res) => setDraft({ subject: res.subject, to: res.to, body: res.body }),
  });

  return (
    <div style={{ padding: 'var(--spacing-4)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--spacing-4)',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Enquiries</h1>
          <p style={{ color: 'var(--color-text-muted)', margin: 'var(--spacing-1) 0 0' }}>
            Inbox emails from info@ · AI drafts a reply into Gmail Drafts for you to review.
          </p>
        </div>
        <button
          style={primaryButtonStyle}
          disabled={ingestMutation.isPending}
          onClick={() => ingestMutation.mutate()}
        >
          {ingestMutation.isPending ? 'Refreshing…' : 'Refresh inbox'}
        </button>
      </div>

      {draftMutation.isError && (
        <div style={{ ...pageCardStyle, marginBottom: 'var(--spacing-3)', color: 'var(--color-error)' }}>
          {(draftMutation.error as Error).message}
        </div>
      )}

      <section style={pageCardStyle}>
        <ResponsiveList
          rows={isLoading ? [] : emails}
          getRowKey={(e) => e.gmail_id}
          emptyMessage={isLoading ? 'Loading…' : 'No emails ingested yet — click Refresh inbox.'}
          columns={[
            { key: 'from', label: 'From', primaryOnMobile: true, render: (e) => e.from_name || e.from_email },
            { key: 'subject', label: 'Subject', render: (e) => e.subject ?? '—' },
            {
              key: 'received',
              label: 'Received',
              render: (e) => (e.received_at ? new Date(e.received_at).toLocaleDateString('en-AU') : '—'),
              hideOnMobile: true,
            },
            {
              key: 'matched',
              label: 'Customer',
              render: (e) =>
                e.matched ? (
                  <span style={{ color: 'var(--color-success)' }}>Matched</span>
                ) : (
                  <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                ),
            },
            {
              key: 'action',
              label: '',
              align: 'right',
              render: (e) => (
                <button
                  style={secondaryButtonStyle}
                  disabled={draftMutation.isPending}
                  onClick={() => draftMutation.mutate(e.gmail_id)}
                >
                  {draftMutation.isPending && draftMutation.variables === e.gmail_id
                    ? 'Drafting…'
                    : 'Draft reply'}
                </button>
              ),
            },
          ]}
        />
      </section>

      {draft && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--spacing-4)',
          }}
          onClick={() => setDraft(null)}
        >
          <div style={{ ...pageCardStyle, width: 'min(640px, 94vw)', maxHeight: '88vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>Draft created in Gmail ✅</h2>
            <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>
              To: {draft.to} · {draft.subject} — saved to <strong>info@ Drafts</strong> for you to review &amp; send.
            </p>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-default)',
                padding: 'var(--spacing-3)',
              }}
            >
              {draft.body}
            </pre>
            <div style={{ textAlign: 'right' }}>
              <button style={primaryButtonStyle} onClick={() => setDraft(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
